/**
 * caseObservation.js — ONE OWNER FOR "IS THIS CASE UNDER OBSERVATION", AND FOR THE
 * BRIDGE TYPE THAT FOLLOWS FROM IT.
 *
 * ★★★ WHY THIS EXISTS. DeliverScreen.jsx answered this question in SIX places with FOUR
 * different predicates (CHECKUP-MODAL-REUSE-1, session 91):
 *
 *   :920   status === 'in_observation' || followUp?.type === 'observation'      (2 signals)
 *   :1394  isFollowUpCase — eight signals, three of them observation             (3 signals)
 *   :1418  status === 'in_observation' ? 'observation_checkin' : ...             (1 signal)
 *   :1476  status === 'in_observation' ? 'observation_checkin' : ...             (1 signal)
 *   :1561  getDeliverState() — deliverData.followUpPlan === 'observation'        (a DIFFERENT FIELD)
 *   :1775  status === 'in_observation' || followUp?.type === 'observation'      (2 signals)
 *
 * ★★★ AND THE DISAGREEMENT WAS LOAD-BEARING, NOT COSMETIC. The Follow-Up Report card is
 * gated `isFollowUpCase && deliverState !== 'observation'`. Those two read DIFFERENT
 * FIELDS, so a case whose `status` is `in_observation` while `deliverData.followUpPlan`
 * is unset satisfies both halves: the monitored patient is shown a card marked
 * **Required** that opens the voice gate and asks for a memo nobody needs — which is the
 * first horn of CHECKUP-MODAL-REUSE-1, live, while the board recorded it as avoided.
 *
 * ★★ THE FAILURE MODE IS A LEGAL-LOOKING VALUE (§1dk(e)). Neither answer throws; each is
 * a plausible boolean; the only symptom is a patient being asked for something, which
 * looks exactly like a patient being asked for something on purpose.
 *
 * THE RULE: an observation signal ANYWHERE makes it an observation. This is a READ, and a
 * read may fall back (D414). The asymmetry is deliberate — mistaking a follow-up for an
 * observation costs a question set; mistaking an observation for a follow-up costs a
 * monitored patient a weekly voice memo, which is the defect this owner was written for.
 *
 * ★★ `getDeliverState()` IS NOT CHANGED BY THIS OWNER AND MUST NOT BE. Its other
 * consumers — the progress bar, the care-complete card, the status label — key off
 * `followUpPlan` alone and were measured correct on it. Widening that string would move
 * four surfaces to fix one, which is how a repair becomes an outage (D428, D430).
 */

const OBSERVATION = 'observation'

/**
 * Is this case under observation?
 * @param {object} caseData    the case (`currentCase`)
 * @param {object} [deliverData] the deliver payload, when the caller has it — carries
 *                 `followUpPlan`, which is the ONLY signal `getDeliverState()` reads.
 */
export function isObservationCase(caseData, deliverData) {
  if (!caseData && !deliverData) return false
  return !!(
    caseData?.status === 'in_observation' ||
    caseData?.followUp?.type === OBSERVATION ||
    caseData?.followUp?.disposition === OBSERVATION ||
    deliverData?.followUpPlan === OBSERVATION
  )
}

/**
 * Which bridge-question set is being asked for.
 *
 * ★★★ THE POINT OF THE ROW: the set is chosen by WHAT IS BEING ASKED, not by WHICH FLOW
 * IS ASKING. `FollowUpReportModal` used to carry `const bridgeType = 'follow_up_checkin'`
 * as a typed constant behind an `isFollowUp` guard, so the same modal opened on an
 * observation case asked follow-up questions and could not be told otherwise.
 */
export function bridgeTypeFor(caseData, deliverData) {
  return isObservationCase(caseData, deliverData) ? 'observation_checkin' : 'follow_up_checkin'
}
